import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { buildTheme } from "../theme/buildTheme";

/**
 * Theme provider for the public organisation pages.
 *
 * Same design as the main app, painted in the organisation's brand colours.
 * Kept separate from ThemeContextProvider because the public tree has no Kinde
 * and no cookie-backed app preferences - just the organisation's default and
 * whatever the visitor has since chosen.
 */

const OrgThemeContext = createContext({ mode: "light", toggleTheme: () => {} });

export const useOrgTheme = () => useContext(OrgThemeContext);

const MODE_KEY = "org-theme-mode";

function readStoredMode() {
  try {
    const stored = window.localStorage.getItem(MODE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Private browsing, or site data blocked. Fall back to the default.
    return null;
  }
}

function writeStoredMode(mode) {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Not being able to remember the choice is not worth failing over.
  }
}

export const OrgThemeProvider = ({ organisation, children }) => {
  const [mode, setMode] = useState(() => readStoredMode() || "light");

  // The organisation arrives after first paint, so adopt its preferred mode
  // unless the visitor has already chosen one for themselves.
  useEffect(() => {
    if (!organisation?.default_mode) return;
    if (readStoredMode()) return;
    setMode(organisation.default_mode);
  }, [organisation]);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      writeStoredMode(next);
      return next;
    });
  };

  const theme = useMemo(
    () =>
      buildTheme(mode, {
        primary: organisation?.primary_color,
        secondary: organisation?.secondary_color,
        accent: organisation?.accent_color,
      }),
    [mode, organisation],
  );

  const value = useMemo(() => ({ mode, toggleTheme }), [mode]);

  return (
    <OrgThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </OrgThemeContext.Provider>
  );
};
